import { VossVersionStatus } from '../../../Constants/VOSS/vossVersionStatus';
import { allowedTransitions } from './packageConstants';
import type { PackageVersion } from './Packages.types';

export interface NumberedVersion extends PackageVersion {
    number: number;
    label: string;
}

// VOSS has no version number and documents no ordering, so derive it from created date rather than array position
export const numberVersions = (versions: PackageVersion[]): NumberedVersion[] =>
    [...versions]
        .sort((a, b) => new Date(a.created ?? 0).getTime() - new Date(b.created ?? 0).getTime())
        .map((version, index) => ({ ...version, number: index + 1, label: `v${index + 1}` }))
        .reverse();

export const versionLabelOf = (versions: NumberedVersion[], versionId: string | undefined): string =>
    versionId === undefined ? '' : (versions.find((version) => version.versionId === versionId)?.label ?? '');

const NEXT_STATUS: Partial<Record<VossVersionStatus, VossVersionStatus>> = {
    [VossVersionStatus.Draft]: VossVersionStatus.Staged,
    [VossVersionStatus.Staged]: VossVersionStatus.Published,
};

// the one forward step that gets the primary button; the other allowed transitions go in the overflow menu
export const nextVersionStatus = (current: VossVersionStatus | undefined): VossVersionStatus | undefined => (current === undefined ? undefined : NEXT_STATUS[current]);

export const secondaryVersionTransitions = (current: VossVersionStatus | undefined): VossVersionStatus[] =>
    allowedTransitions(current).filter((status) => status !== nextVersionStatus(current));

export interface VersionTransitionConfig {
    actionKey: string;
    titleKey: string;
    textKey: string;
    confirmKey: string;
    successKey: string;
    icon: string;
    destructive: boolean;
    requiresAcknowledgement: boolean;
}

// keyed by the target status
export const versionTransitionConfig: Record<VossVersionStatus, VersionTransitionConfig> = {
    [VossVersionStatus.Draft]: {
        actionKey: 'packages.restoreToDraft',
        titleKey: 'packages.restoreToDraftTitle',
        textKey: 'packages.restoreToDraftText',
        confirmKey: 'packages.restoreToDraft',
        successKey: 'packages.versionRestored',
        icon: 'undo',
        destructive: false,
        requiresAcknowledgement: false,
    },
    [VossVersionStatus.Staged]: {
        actionKey: 'packages.promoteToStaged',
        titleKey: 'packages.promoteToStagedTitle',
        textKey: 'packages.promoteToStagedText',
        confirmKey: 'packages.promoteToStaged',
        successKey: 'packages.versionPromoted',
        icon: 'forward',
        destructive: false,
        requiresAcknowledgement: false,
    },
    [VossVersionStatus.Published]: {
        actionKey: 'packages.publishVersion',
        titleKey: 'packages.publishVersionTitle',
        textKey: 'packages.publishVersionText',
        confirmKey: 'packages.publishVersionConfirm',
        successKey: 'packages.versionPublished',
        icon: 'upload',
        destructive: false,
        requiresAcknowledgement: true,
    },
    [VossVersionStatus.Archived]: {
        actionKey: 'packages.archiveVersion',
        titleKey: 'packages.archiveVersionTitle',
        textKey: 'packages.archiveVersionText',
        confirmKey: 'packages.archiveVersion',
        successKey: 'packages.versionArchived',
        icon: 'lock',
        destructive: true,
        requiresAcknowledgement: false,
    },
    [VossVersionStatus.Discarded]: {
        actionKey: 'packages.discardVersion',
        titleKey: 'packages.discardVersionTitle',
        textKey: 'packages.discardVersionText',
        confirmKey: 'packages.discardVersion',
        successKey: 'packages.versionDiscarded',
        icon: 'close-circle',
        destructive: true,
        requiresAcknowledgement: false,
    },
};
